import { Info, Warning, WarningOctagon } from '@phosphor-icons/react';
import type { TenantBanner } from '@brechoflow/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { usePermission, useSession } from '@/features/auth/session';
import { cn } from '@/lib/cn';
import { sqk, subscriptionApi } from './api';

type Kind = NonNullable<TenantBanner['kind']>;

const TONE: Record<Kind, { box: string; icon: typeof Info }> = {
  trial: { box: 'bg-primary/10 text-foreground', icon: Info },
  past_due: { box: 'bg-warning-soft text-warning', icon: Warning },
  grace: { box: 'bg-warning-soft text-warning', icon: Warning },
  canceled: { box: 'bg-warning-soft text-warning', icon: Warning },
  suspended: { box: 'bg-destructive-soft text-destructive', icon: WarningOctagon },
  pending_deletion: { box: 'bg-destructive-soft text-destructive', icon: WarningOctagon },
};

export function bannerText(b: TenantBanner): string {
  if (b.message) return b.message;
  const d = b.daysLeft;
  switch (b.kind) {
    case 'trial':
      return d != null ? `Seu período de teste termina em ${d} ${d === 1 ? 'dia' : 'dias'}.` : 'Você está no período de teste.';
    case 'past_due':
      return 'Há um pagamento em atraso. Regularize para evitar a suspensão.';
    case 'grace':
      return d != null ? `Pagamento em atraso: a conta será suspensa em ${d} ${d === 1 ? 'dia' : 'dias'}.` : 'Pagamento em atraso: a conta será suspensa em breve.';
    case 'suspended':
      return 'Conta suspensa: somente leitura. Regularize a assinatura para voltar a editar.';
    case 'canceled':
      return 'A assinatura foi cancelada.';
    case 'pending_deletion':
      return 'A exclusão desta conta está agendada.';
    default:
      return '';
  }
}

/** Faixa global com a situação da assinatura. Qualquer usuário da loja vê; só quem gerencia vai para a tela de assinatura. */
export function SubscriptionBanner() {
  const { membership } = useSession();
  const canManage = usePermission('settings.manage');
  const q = useQuery({ queryKey: sqk.banner(membership?.organizationId), queryFn: subscriptionApi.banner, staleTime: 60_000, retry: false });
  const b = q.data;
  if (!b?.kind) return null;
  const { box, icon: Icon } = TONE[b.kind];
  return (
    <div role="status" data-testid="subscription-banner" data-kind={b.kind} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2.5 text-sm sm:px-8', box)}>
      <Icon className="size-5 shrink-0" weight="fill" aria-hidden />
      <p className="min-w-0 flex-1 font-medium">{bannerText(b)}</p>
      {canManage ? (
        <Link to="/configuracoes/assinatura" className="shrink-0 font-semibold underline underline-offset-4">
          Ver assinatura
        </Link>
      ) : (
        <span className="shrink-0 text-xs opacity-80">Fale com quem administra a loja.</span>
      )}
    </div>
  );
}
